import React, { useEffect, useRef, useState } from "react";
import { keyframes } from "glamor";
import { FaRobot } from "react-icons/fa";
import { css, spacing, typographyColors, uiColors } from "../library";
import { colors } from "../library/theme";
import ChatMessage from "./ChatMessage";

// Tom mais forte do accent para o gradiente do "spark" (não há token exato).
const accentStrong = "#c73b34";
const accentGlow = "rgba(224, 73, 66, 0.40)";
const inkFaint = colors.greyLight;

const list = css({
  flex: 1,
  overflowY: "auto",
  padding: `26px ${spacing.m}`,
  display: "flex",
  flexDirection: "column",
  gap: spacing.m,
  // Só a cor da barra muda; a espessura continua a nativa do navegador. Sem
  // isto o thumb sai na cor de destaque do sistema (azul), que destoa do
  // vermelho da plataforma.
  //
  // Usamos apenas `scrollbar-color`, que é padrão e funciona em Firefox e
  // Chrome 121+. De propósito não há regra `::-webkit-scrollbar`: no Chrome,
  // qualquer uma delas desliga a barra nativa e passa a exigir que TODAS as
  // partes sejam estilizadas, o que muda a aparência bem além da cor.
  // O preço é não ter um tom próprio para "arrastando" — a propriedade padrão
  // ainda não tem esse estado.
  scrollbarColor: `${colors.greyLight} transparent`,
});
const hero = css({
  margin: "auto",
  textAlign: "center",
  maxWidth: 460,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 14,
  padding: `${spacing.m} 0`,
});
const spark = css({
  width: 62,
  height: 62,
  borderRadius: "50%",
  display: "grid",
  placeItems: "center",
  color: colors.white,
  background: `radial-gradient(120% 120% at 30% 20%, ${uiColors.theme} 0%, ${accentStrong} 100%)`,
  boxShadow: `0 8px 20px ${accentGlow}`,
});
const heroTitle = css({
  margin: 0,
  fontSize: 19,
  fontWeight: 600,
  color: colors.greyDarker,
});
const heroText = css({
  margin: 0,
  fontSize: 13.5,
  lineHeight: 1.6,
  color: typographyColors.subtitle,
});
const suggestions = css({
  display: "grid",
  // auto-fit: um único atalho ocupa a linha toda; vários se distribuem.
  gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
  gap: 10,
  width: "100%",
  marginTop: 6,
});
const chip = css({
  fontFamily: "inherit",
  textAlign: "left",
  fontSize: 13,
  fontWeight: 300,
  color: colors.greyDarker,
  cursor: "pointer",
  backgroundColor: colors.greyLightest,
  border: `1px solid ${uiColors.border}`,
  borderRadius: 12,
  padding: "12px 14px",
  lineHeight: 1.35,
  transition: "transform .12s ease, border-color .12s ease, background .12s ease",
  ":hover": {
    borderColor: colors.decorationSoft,
    transform: "translateY(-2px)",
    backgroundColor: "#fdf3f2",
  },
  ":focus-visible": { outline: `2px solid ${uiColors.theme}`, outlineOffset: 2 },
});

const blink = keyframes({
  "0%, 80%, 100%": { opacity: 0.25, transform: "translateY(0)" },
  "40%": { opacity: 1, transform: "translateY(-3px)" },
});
const typingRow = css({ display: "flex", gap: 11, alignItems: "flex-end" });
const typingAvatar = css({
  flex: "0 0 30px",
  width: 30,
  height: 30,
  borderRadius: "50%",
  display: "grid",
  placeItems: "center",
  backgroundColor: uiColors.theme,
  color: colors.white,
  marginBottom: 2,
});
const typing = css({
  display: "inline-flex",
  gap: 4,
  padding: "14px 16px",
  backgroundColor: colors.greyLightest,
  borderRadius: 16,
  borderBottomLeftRadius: 5,
});
const dot = css({
  width: 7,
  height: 7,
  borderRadius: "50%",
  backgroundColor: inkFaint,
  animation: `${blink} 1.2s infinite ease-in-out`,
});
const dot2 = css({ animationDelay: ".2s" });
const dot3 = css({ animationDelay: ".4s" });

const waitHint = css({
  margin: "2px 0 0 41px",
  fontSize: 12.5,
  lineHeight: 1.5,
  color: typographyColors.subtitle,
});

// O primeiro token costuma chegar por volta dos 6s, então o aviso espera um
// pouco mais que isso: em resposta normal ele nem chega a aparecer. Avisar antes
// faria a frase piscar na tela e o assistente parecer lento quando não está.
const LONG_WAIT_MS = 9000;

// Folga para considerar que o aluno ainda está "no fim" da conversa.
const STICK_THRESHOLD_PX = 80;

export default function ChatMessageList({
  messages,
  sending,
  streamingMessageId,
  emptyMessage,
  suggestions: starters = [],
  onSuggestion,
}) {
  const listRef = useRef(null);
  // Se o aluno está acompanhando o fim da conversa. Guardado num ref e
  // atualizado quando ELE rola, nunca depois de mensagem nova entrar: medir
  // após o conteúdo crescer daria "longe do fim" justamente por causa da
  // mensagem que acabou de chegar, e o chat pararia de acompanhar sozinho.
  const stickToBottom = useRef(true);
  const [longWait, setLongWait] = useState(false);

  // Durante o streaming o conteúdo da ÚLTIMA mensagem cresce sem que
  // messages.length mude — por isso o tamanho do texto entra nas dependências.
  const last = messages[messages.length - 1];
  const lastLength = last ? last.text.length : 0;

  // Em streaming, a bolha só existe depois do primeiro pedaço de texto. Até lá
  // — e no modo de resposta única, o tempo todo — quem mostra que algo está
  // acontecendo é o indicador de "digitando".
  const replyStarted = messages.some(
    (message) => message.id === streamingMessageId
  );
  const waiting = sending && !replyStarted;

  useEffect(() => {
    if (!waiting) {
      setLongWait(false);
      return undefined;
    }

    const timer = setTimeout(() => setLongWait(true), LONG_WAIT_MS);
    return () => clearTimeout(timer);
  }, [waiting]);

  // Enquanto o aluno não subir para reler algo, o chat acompanha o fim sozinho.
  function handleScroll() {
    const node = listRef.current;
    if (!node) return;

    stickToBottom.current =
      node.scrollHeight - node.scrollTop - node.clientHeight < STICK_THRESHOLD_PX;
  }

  // Rola o próprio container: scrollIntoView levaria a janela inteira para o fim
  // da página e esconderia o cabeçalho.
  useEffect(() => {
    const node = listRef.current;
    if (node && stickToBottom.current) node.scrollTop = node.scrollHeight;
    // `longWait` entra porque o aviso de espera também muda a altura da lista.
  }, [messages.length, lastLength, sending, longWait]);

  const isEmpty = messages.length === 0 && !sending;

  return (
    <div className={list} ref={listRef} onScroll={handleScroll}>
      {isEmpty ? (
        <div className={hero}>
          <div className={spark}>
            <FaRobot size={26} />
          </div>
          <h3 className={heroTitle}>Como posso te ajudar?</h3>
          <p className={heroText}>{emptyMessage}</p>
          {starters.length > 0 && onSuggestion && (
            <div className={suggestions}>
              {starters.map((text) => (
                <button
                  key={text}
                  type="button"
                  className={chip}
                  onClick={() => onSuggestion(text)}
                >
                  {text}
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        messages.map((message) => (
          <ChatMessage
            key={message.id}
            message={message}
            streaming={message.id === streamingMessageId}
          />
        ))
      )}
      {/* Assim que o texto começa a chegar, a própria bolha crescendo já é o
          feedback — manter os pontinhos ao lado dela ficaria redundante. */}
      {waiting && (
        <div>
          <div className={typingRow}>
            <div className={typingAvatar}>
              <FaRobot size={15} />
            </div>
            <div className={typing}>
              <span className={dot} />
              <span className={css(dot, dot2)} />
              <span className={css(dot, dot3)} />
            </div>
          </div>
          {longWait && (
            <p className={waitHint}>
              Respostas mais elaboradas podem levar mais tempo.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
